// The Workflows area's ONE status vocabulary and ONE set of run counts,
// pure (no React) so status.selftest.ts runs it on its own. Every screen
// that says how a run, a stage, a branch, a job or a step stands reads it
// here: the workflow cards, the Runs table, the Jobs table and the job
// page, the run view (header, cards, timeline, events, legend), the phone.
//
// Glossary: a WORKFLOW is a saved pipeline; a RUN is one go at it; a run
// has STEPS (the engine's stages), and a step that splits up runs once per
// item, each a COPY (the engine's branches); the checkpoints a subagent
// records inside a step are PARTS. A job (a run the Lead planned) says the
// same words. Types, props and API fields keep the engine's names.
//
// A status is a WORD, a TONE (StatusBadge's colours: live work is always
// the theme's live colour, its accent unless it sets its own), an ICON name (StatusMark.tsx draws it) and a LOOK for the
// bars: live work pulses, waiting work is faint, work that never ran
// (skipped, cancelled) is hatched, the rest are solid.

import type { NodeState, RunCounters } from "@/api/workflowRuns";
import type { StatusTone } from "@/components/ui/StatusBadge";
import { pluralize } from "@/lib/format";

export type { StatusTone };

export type StatusIconName = "waiting" | "running" | "paused" | "done" | "warning" | "failed" | "timedOut" | "cancelled" | "skipped" | "planning" | "approval" | "unknown";

/** How a status is drawn on a bar, a tick or a swatch. */
export type StatusLook = "live" | "solid" | "waiting" | "notRun";

export interface StatusWord {
  label: string;
  tone: StatusTone;
  icon: StatusIconName;
  look: StatusLook;
}

/** A status word of one's own (a check's outcome, a fault's severity) in the same shape: solid unless said. */
export function makeStatusWord(label: string, tone: StatusTone, icon: StatusIconName, look: StatusLook = "solid"): StatusWord {
  return { label, tone, icon, look };
}

const word = makeStatusWord;

const WAITING = word("Waiting", "neutral", "waiting", "waiting");
const RUNNING = word("Running", "live", "running", "live");
const DONE = word("Done", "success", "done");

/**
 * Every status a run, a node, a stage or a job can have, by its engine
 * name. A run and a node that share a name share the word ("completed" is
 * Done for both); "queued", "pending" and "ready" are all Waiting.
 */
export const STATUS: Readonly<Record<string, StatusWord>> = {
  // a run and its nodes
  queued: WAITING,
  pending: WAITING,
  ready: WAITING,
  starting: word("Starting", "live", "running", "live"),
  running: RUNNING,
  paused: word("Paused", "warning", "paused"),
  completed: DONE,
  completed_with_failures: word("Done, some failed", "warning", "warning"),
  failed: word("Failed", "danger", "failed"),
  // amber, not red: it ran out of its time (the job page's overrun colour)
  timed_out: word("Timed out", "warning", "timedOut"),
  cancelled: word("Stopped", "neutral", "cancelled", "notRun"),
  skipped: word("Skipped", "neutral", "skipped", "notRun"),
  // a job's own states
  planning: word("Planning", "live", "planning", "live"),
  awaiting_approval: word("Waiting for you", "warning", "approval"),
  finishing: word("Finishing", "live", "running", "live"),
  done: DONE,
};

const UNKNOWN = word("Unknown", "neutral", "unknown");

/** The word, tone, icon and look of any status; "Unknown" for one this dashboard does not know. */
export function statusWord(status: string | null | undefined): StatusWord {
  return (status && STATUS[status]) || UNKNOWN;
}

/** What the Runs list knows of a job run (RunSummary.job), and the Jobs list of a job. */
export interface JobMark {
  status?: string | null;
  failed_checks?: number | null;
}

/** What failed in a run or a stage, for its word. */
export interface FailCounts {
  failed?: number | null;
  timed_out?: number | null;
}

/** "Done, 1 failed": one wording for a run or a stage that finished with failures. */
export function doneFailedWord(n: number): StatusWord {
  return word(`Done, ${n} failed`, "warning", "warning");
}

/**
 * A run's word, the same on the Runs list, the workflow cards, the Jobs
 * list, the job page and the run view. A job run says the job's word
 * ("Planning", "Waiting for you", "Finishing") unless its run has ended
 * otherwise (cancelled, failed, paused, done with failures), and a job
 * that finished with checks that failed says so ("Done – 1 check failed").
 */
export function runStatusWord(runStatus: string | null | undefined, job?: JobMark | null, counters?: FailCounts | null): StatusWord {
  const js = job?.status;
  if (runStatus === "completed_with_failures" && counters) {
    const n = (counters.failed ?? 0) + (counters.timed_out ?? 0);
    if (n > 0) return doneFailedWord(n);
  }
  if (!js) return statusWord(runStatus);
  if (runStatus === "paused" && js === "awaiting_approval") return STATUS.awaiting_approval;
  if (runStatus === "cancelled" || runStatus === "failed" || runStatus === "paused" || runStatus === "completed_with_failures") return statusWord(runStatus);
  const failedChecks = job?.failed_checks ?? 0;
  if (failedChecks > 0 && runStatus === "completed") return word(`Done – ${pluralize(failedChecks, "check")} failed`, "warning", "warning");
  return STATUS[js] ?? statusWord(runStatus);
}

/** A run's word from its summary: a task that has not started waits for its OK, though its run reads paused. */
export function runSummaryWord(run: { status?: string | null; job?: JobMark | null; counters?: FailCounts | null; needs_ok?: boolean }): StatusWord {
  return run.needs_ok ? STATUS.awaiting_approval : runStatusWord(run.status, run.job, run.counters);
}

/**
 * The run view's word for its run (RunDetail): the api's job mark, failed
 * checks included, so a job that finished with a failed check says "Done –
 * 1 check failed" there as on the Runs and Jobs lists; the run's own job
 * status when the mark is missing (an older api).
 */
export function runDetailWord(detail: { run: { status?: string | null; job?: { status?: string | null } | null }; job?: JobMark | null; counters?: FailCounts | null }): StatusWord {
  const { run } = detail;
  return runStatusWord(run.status, detail.job ?? (run.job ? { status: run.job.status } : null), detail.counters);
}

// ── what a status means ──

/** A run the engine is still working on (or holding). */
export function isLiveRun(status: string | null | undefined): boolean {
  return status === "queued" || status === "running" || status === "paused";
}

/** The engine is working on the node right now. */
export function isLiveNode(status: string | null | undefined): boolean {
  return status === "starting" || status === "running";
}

export function isWaitingNode(status: string | null | undefined): boolean {
  return status === "pending" || status === "ready";
}

export function hasEnded(status: string | null | undefined): boolean {
  return !!status && !isLiveNode(status) && !isWaitingNode(status);
}

/** Ended in an error or out of time (the two failures; cancelled and skipped work never is one). */
export function isFailedNode(status: string | null | undefined): boolean {
  return status === "failed" || status === "timed_out";
}

/** Work that never ran, or was stopped before it finished: skipped or cancelled. */
export function isNotRun(status: string | null | undefined): boolean {
  return statusWord(status).look === "notRun";
}

// ── a stage ──

/** A stage's status: a node's, or "completed_with_failures" for a fan-out that finished with failed branches. */
export type StageStatus = NodeState["status"] | "completed_with_failures";

/**
 * One status for a whole stage, the rule the backend's counters use too
 * (services/workflows/schema.py stage_status). Anything live → running;
 * anything still to start, or no node yet (a fan-out whose items are not
 * known) → waiting. Every node ended: some completed and none cancelled →
 * done (done with failures when a branch failed or timed out); else
 * failed, timed out, cancelled, skipped in that order (a stage stopped
 * because a branch failed says failed, not cancelled).
 */
export function stageStatus(nodes: readonly Pick<NodeState, "status">[]): StageStatus {
  if (!nodes.length) return "pending";
  if (nodes.some((n) => isLiveNode(n.status))) return "running";
  if (nodes.some((n) => !hasEnded(n.status))) return "pending";
  const has = (s: string) => nodes.some((n) => n.status === s);
  if (has("completed") && !has("cancelled")) return nodes.some((n) => isFailedNode(n.status)) ? "completed_with_failures" : "completed";
  if (has("failed")) return "failed";
  if (has("timed_out")) return "timed_out";
  if (has("cancelled")) return "cancelled";
  return "skipped";
}

/** A stage counts as done only when it finished: done, or done with failed branches handed on. */
export function isStageDone(status: string | null | undefined): boolean {
  return status === "completed" || status === "completed_with_failures";
}

// ── the run's counts ──

/** One part of the progress line; `tone` colours the failures (red), time-outs (amber) and what never ran (grey). */
export interface ProgressPart {
  text: string;
  tone: StatusTone | null;
}

/**
 * One vocabulary, the editor's, for a workflow run and a job alike: the
 * boxes are steps, a step's parallel copies are copies, and the checkpoints
 * a subagent records INSIDE a step are parts ("Part 2 of 5"). `job` is
 * kept for the callers that still say which run they are.
 */
export function unitWords(_job?: boolean): { stage: [string, string]; branch: [string, string]; step: [string, string] } {
  return { stage: ["step", "steps"], branch: ["copy", "copies"], step: ["part", "parts"] };
}

/** The run view's nouns: steps and copies (unitWords). */
export interface RunWords {
  stage: string;
  stages: string;
  branch: string;
  branches: string;
  Stage: string;
  Stages: string;
  Branch: string;
  Branches: string;
  /** One piece of work, a step's run or a copy: "step". */
  stageRun: string;
}

const upperFirst = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export function runWords(job?: boolean): RunWords {
  const u = unitWords(job);
  const [stage, stages] = u.stage;
  const [branch, branches] = u.branch;
  return { stage, stages, branch, branches, Stage: upperFirst(stage), Stages: upperFirst(stages), Branch: upperFirst(branch), Branches: upperFirst(branches), stageRun: "step" };
}

/**
 * The run's progress from its counters (the backend's ONE count, the same
 * on every screen; services/workflows/schema.py progress_line says it the
 * same way for the agents and the bell): "3 of 7 steps done · 12 copies
 * · 1 failed · 1 timed out · 2 cancelled · 1 skipped". A step is done only
 * when it finished; failed, timed out, cancelled and skipped count pieces
 * of work (a stage's run, or a branch), each apart. `running` adds "N
 * running" (the header shows it with its own pulse instead); `copies:
 * false` leaves out the copies (the run header: the graph shows them).
 */
export function progressParts(c: RunCounters, { job = false, running = false, copies = true }: { job?: boolean; running?: boolean; copies?: boolean } = {}): ProgressPart[] {
  const u = unitWords(job);
  const parts: ProgressPart[] = [{ text: `${c.stages_done} of ${c.stages_total} ${c.stages_total === 1 ? u.stage[0] : u.stage[1]} done`, tone: null }];
  const branches = copies ? c.branches ?? 0 : 0;
  if (branches) parts.push({ text: `${branches} ${branches === 1 ? u.branch[0] : u.branch[1]}`, tone: null });
  if (running && c.live) parts.push({ text: `${c.live} running`, tone: "live" });
  if (c.failed) parts.push({ text: `${c.failed} failed`, tone: "danger" });
  if (c.timed_out) parts.push({ text: `${c.timed_out} timed out`, tone: STATUS.timed_out.tone });
  if (c.cancelled) parts.push({ text: `${c.cancelled} cancelled`, tone: "neutral" });
  if (c.skipped) parts.push({ text: `${c.skipped} skipped`, tone: "neutral" });
  return parts;
}

/** The progress line as plain text (an aria-label, a tooltip). */
export function progressText(c: RunCounters, opts?: { job?: boolean; running?: boolean; copies?: boolean }): string {
  return progressParts(c, opts)
    .map((p) => p.text)
    .join(" · ");
}

// ── the legend ──

/** The states the run view draws, in the legend's order: live, the endings, then what waits and what never ran. */
export const STATUS_LEGEND: readonly { status: string; word: StatusWord }[] = ["running", "completed", "failed", "timed_out", "pending", "skipped", "cancelled"].map((status) => ({
  status,
  word: statusWord(status),
}));

// ── events ──

/**
 * An event's tone: a run's or a node's status event (run_completed,
 * node_timed_out…) takes that status's tone, so an event never says a
 * colour its card does not; anything else by what it describes.
 */
export function eventTone(kind: string): StatusTone {
  const m = /^(run|node)_(.+)$/.exec(kind);
  if (m && STATUS[m[2]]) return STATUS[m[2]].tone;
  if (/fail|error|lost/.test(kind)) return "danger";
  if (/timed_out|timeout/.test(kind)) return STATUS.timed_out.tone;
  if (/complet|done|finish|report/.test(kind)) return "success";
  if (/cancel|skip/.test(kind)) return "neutral";
  if (/pause/.test(kind)) return "warning";
  if (/start|resume|retry|continu|nudge|wait|expand|fan/.test(kind)) return "live";
  return "neutral";
}
